import { defineComponent as _defineComponent } from 'vue'
import {
  PropType, ref, Ref, watch, onMounted, computed, inject,
} from 'vue';
import { paymentMethodInfo } from '@/services/billing-utils';
import store from '@/store/store';
import {
  Customer, CustomerBalanceStatus, Invoice, SetupIntent, PaymentMethod, PaymentMethodType,
} from '@/api/api-billing';
import { StripeElements } from '@stripe/stripe-js';
import { PaymentWizardMode, PaymentWizardStep, PaymentWizardStepKey } from '@/services/payment-wizard-utils';
import { NEW_PAYMENT_METHOD, PAYMENT_METHOD_BANK_TRANSFER, confirmSetup } from '@/services/payment-details-utils';
import i18n from '@/plugins/i18n';
import { performAction } from '@/services/vue-utils';
import PaymentElement, { PaymentElementsCompleteEvent } from './PaymentElement.vue';
import PaymentMethodsList from './PaymentMethodsList.vue';

export interface NewPaymentMethodEvent {
  finishedSetupIntent: SetupIntent,
}

export interface PaymentMethodChangedEvent {
  paymentMethod: StripeElements | string;
  paymentMethodType: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentMethods',
  props: {
  mode: { type: String as PropType<PaymentWizardMode>, default: undefined },
  clientSecret: { type: Object as PropType<string|undefined>, default: undefined },
  invoice: { type: Object as PropType<Invoice|undefined>, required: false, default: undefined },
  defaultActivate: { type: Boolean as PropType<boolean>, default: true },
  disableActivate: { type: Boolean as PropType<boolean>, default: false },
  hideActivate: { type: Boolean as PropType<boolean>, default: false },
  disallowNew: { type: Boolean as PropType<boolean>, default: false },
  allowedPaymentMethodTypes: { type: Array as PropType<string[]|undefined>, default: undefined },
  hideTitle: { type: Boolean, required: false, default: false },
},
  emits: ["activate-changed", "changed"],
  setup(__props, { emit }: { emit: ({
  (e: 'activate-changed', event: boolean): void
  (e: 'changed', event: PaymentMethodChangedEvent | undefined): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;



// eslint-disable-next-line no-spaced-func, func-call-spacing


const selected = ref('') as Ref<string>; // selected payment method id or new

// new payment method if complete (all required fields provided)
const newElements = ref(undefined) as Ref<StripeElements|undefined>;
const newPaymentMethodType = ref(undefined) as Ref<string|undefined>;

const customer = computed(() => (store.getters.billingCustomer ?? undefined) as Customer|undefined);

const bankTransferAllowed = computed(() => customer.value?.customerBalanceStatus === CustomerBalanceStatus.Granted
  && (!props.allowedPaymentMethodTypes || props.allowedPaymentMethodTypes.includes(PaymentMethodType.CustomerBalance)));

const otherThanBankTransferAllowed = computed(() => !props.allowedPaymentMethodTypes
|| props.allowedPaymentMethodTypes.some((pmt) => pmt !== PaymentMethodType.CustomerBalance));

const activePaymentMethodID = computed(() => customer.value?.paymentMethod?.ID);

const invoceMode = computed(() => props.mode === PaymentWizardMode.Invoice || props.mode === PaymentWizardMode.Prices);

const paymentMethodsVisible = computed(() => props.mode !== PaymentWizardMode.NewPaymentMethod);

const newPaymentMethodVisible = computed(() => selected.value === NEW_PAYMENT_METHOD);

const disallowNewInactive = computed(() => selected.value === NEW_PAYMENT_METHOD);

const activate = ref(true) as Ref<boolean>;

const forceActivate = ref(false) as Ref<boolean>;

const newPaymentMethodText = computed(() => i18n.tc('button.new_payment_method'));

const paymentMethodInfos = computed(() => {
  let paymentMethods = store.getters.billingPaymentMethods as PaymentMethod[];

  if (props.allowedPaymentMethodTypes) {
    paymentMethods = paymentMethods.filter((pm) => props.allowedPaymentMethodTypes?.includes(pm.type ?? ''));
  }

  const infos = paymentMethods.map((o) => paymentMethodInfo(o)!);
  const apmi = infos.findIndex((ci) => ci.paymentMethod.ID === customer.value?.paymentMethod?.ID);
  if (apmi < 0) return infos;
  const active = infos.splice(apmi);
  active[0].active = true;
  return [...active, ...infos];
});

const paymentMethod = computed(() => {
  if (selected.value === '') return undefined;

  if (selected.value === PAYMENT_METHOD_BANK_TRANSFER) {
    return { paymentMethod: selected.value, paymentMethodType: PaymentMethodType.CustomerBalance };
  }

  if (selected.value !== NEW_PAYMENT_METHOD) {
    const paymentMethodType = paymentMethodInfos.value.find((pmi) => pmi.paymentMethod.ID === selected.value)?.type!;
    if (!paymentMethodType) return undefined;
    return { paymentMethod: selected.value, paymentMethodType };
  }

  if (selected.value === NEW_PAYMENT_METHOD) {
    if (!newElements.value || !newPaymentMethodType.value) return undefined;
    return { paymentMethod: newElements.value, paymentMethodType: newPaymentMethodType.value };
  }

  return undefined;
});

watch(
  () => paymentMethod.value,
  () => { emit('changed', paymentMethod.value); },
  { immediate: true },
);

watch(
  () => paymentMethod.value,
  () => { emit('activate-changed', activate.value); },
  { immediate: true },
);

const onNewComplete = (value: PaymentElementsCompleteEvent | undefined) => {
  selected.value = NEW_PAYMENT_METHOD;
  newElements.value = value?.elements;
  newPaymentMethodType.value = value?.paymentMethodType;
};

onMounted(() => {
  activate.value = props.defaultActivate;

  if (customer.value?.customerBalanceStatus !== CustomerBalanceStatus.Granted // no bank transfer
    && store.getters.billingPaymentMethods.length === 0 // no payment method
    && !props.disableActivate) { // activate is allowed
    activate.value = true;
    forceActivate.value = true;
  }

  if (invoceMode.value && otherThanBankTransferAllowed.value) {
    selected.value = NEW_PAYMENT_METHOD;
  } else if (props.mode === PaymentWizardMode.NewPaymentMethod) {
    selected.value = NEW_PAYMENT_METHOD;
  } else if (bankTransferAllowed.value && !activePaymentMethodID.value) {
    selected.value = PAYMENT_METHOD_BANK_TRANSFER;
  } else if (paymentMethodInfos.value.some((pmi) => pmi.paymentMethod.ID === activePaymentMethodID.value)) {
    selected.value = activePaymentMethodID.value ?? '';
  } else if (paymentMethodInfos.value.length > 0) {
    selected.value = paymentMethodInfos.value[0].paymentMethod.ID!;
  } else if (!props.disallowNew || !otherThanBankTransferAllowed.value) {
    selected.value = NEW_PAYMENT_METHOD;
  } else {
    selected.value = '';
  }
});

// submit new payment method
async function submit() {
  let finishedSetupIntent: SetupIntent | undefined;
  const action = async () => {
    if (!props.clientSecret || !paymentMethod.value) return;
    const { id: intentID } = await confirmSetup(props.clientSecret, paymentMethod.value.paymentMethod, activate.value);
    finishedSetupIntent = await store.dispatch('billingFinishPaymentMethod', { intentID });
  };

  const ok = await performAction(null, i18n.tc('error.general'), action);
  if (!ok) return undefined;

  // return event
  const event = { finishedSetupIntent } as NewPaymentMethodEvent;
  if (finishedSetupIntent) return event;

  return undefined;
}

// wizard embeddable
const addStep = inject('addStep') as ((s: PaymentWizardStep) => void) | undefined;
const stepChanged = inject('stepChanged') as ((s: PaymentWizardStep) => void) | undefined;

const paymentWizardStep = computed(() => (
  {
    key: PaymentWizardStepKey.NewPaymentMethod,
    label: i18n.tc('button.add_new_payment_method'),
    complete: paymentMethod.value !== undefined,
    submitAction: submit,
  } as PaymentWizardStep));

watch(
  () => paymentWizardStep.value,
  () => { if (stepChanged) stepChanged(paymentWizardStep.value); },
);

if (addStep) addStep(paymentWizardStep.value);


return { __sfc: true,props, emit, selected, newElements, newPaymentMethodType, customer, bankTransferAllowed, otherThanBankTransferAllowed, activePaymentMethodID, invoceMode, paymentMethodsVisible, newPaymentMethodVisible, disallowNewInactive, activate, forceActivate, newPaymentMethodText, paymentMethodInfos, paymentMethod, onNewComplete, submit, addStep, stepChanged, paymentWizardStep, PaymentElement, PaymentMethodsList }
}

})